import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  ConfirmModal, Table, TableHeader, TableBody, TableHead, TableRow, TableCell, BulkSelectCell, TableSkeleton,
  RowActions, RowAction, ListPagination, ListSearchInput, ListEmptyRow, BulkSelectHeader, MultiBulkActionButton,
  CustomSelect, MoreMenu, Amount, btnPrimary,
} from '@redpanda/ui';
import {
  useBulkActions, createBulkDeleteOptimisticHandler, createBulkDeleteSuccessHandler, isAdmin, useRefetchOnFocus, useRememberedState,
} from '@redpanda/utils';
import { UserContext } from '../context/UserContext';
import AccountDialog from '../components/AccountDialog';
import { Account, ACCOUNT_TYPES, AccountType, isAccountUsed, useAccountStoreBase } from '../stores/accountStore';
import { bulkDeleteAccounts, createDefaultAccounts, getAccounts } from '../services/api';
import { useListSearch } from '../hooks/useListSearch';
import { notifyDataChanged } from '../utils/dataEvents';
import { pendingRowClass } from '../utils/pendingItem';
import { apiErrorMessage } from '../utils/apiError';

const COLUMNS = 5;
type TypeFilter = AccountType | 'ALL';
const TYPE_FILTERS: readonly TypeFilter[] = ['ALL', ...ACCOUNT_TYPES];

/** 會計科目（科目表）：依代號排序、後端搜尋與類型篩選；已有分錄的科目不能刪 */
const AccountListPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useContext(UserContext)!;
  const userRole = user?.role;
  const admin = isAdmin(userRole);

  const store = useAccountStoreBase();
  const { items: accounts, pagination, isLoading, isPageLoading, showDeleteConfirmModal, isDeleting, fetchItems, setCurrentPage } = store;

  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useRememberedState<TypeFilter>('accounting-ui:accounts.type', 'ALL', TYPE_FILTERS);
  const [dialogAccount, setDialogAccount] = useState<Account | null | undefined>(undefined); // undefined＝關閉、null＝新增
  const [showDefaultsConfirm, setShowDefaultsConfirm] = useState(false);
  const [creatingDefaults, setCreatingDefaults] = useState(false);

  // 有快取先顯示、背景比對（createEntityStore）
  useEffect(() => {
    fetchItems(1);
  }, [fetchItems]);
  useRefetchOnFocus(() => useAccountStoreBase.getState().refreshData());

  // 搜尋、類型篩選由後端查全部科目（R29）；沒有條件時用列表 store
  const search = searchTerm.trim();
  const type = typeFilter === 'ALL' ? undefined : typeFilter;
  const hasFilters = !!search || !!type;
  const searchResult = useListSearch<Account>('accounts', hasFilters ? { search, type } : null, page => getAccounts(page, 20, { search, type }));
  const listItems = searchResult.active ? searchResult.items : accounts;
  const listPagination = searchResult.active ? searchResult.pagination : pagination;
  const listLoading = searchResult.active ? searchResult.isLoading : isPageLoading;

  const bulkDeleteSuccess = createBulkDeleteSuccessHandler(() => useAccountStoreBase.getState(), fetchItems, setCurrentPage);
  const bulkActions = useBulkActions({
    items: listItems,
    idField: 'id',
    entityName: t('accounts.item'),
    bulkDeleteApi: bulkDeleteAccounts,
    canDeleteItem: (account: Account) => admin && !isAccountUsed(account),
    userRole,
    t,
    toast,
    pagination: listPagination ? { currentPage: listPagination.currentPage, totalPages: listPagination.totalPages } : undefined,
    fetchItems,
    setCurrentPage,
    onOptimisticUpdate: createBulkDeleteOptimisticHandler(useAccountStoreBase.setState, 'account', () => accounts, () => pagination),
    onSuccess: async (deletedIds?: string[]) => {
      await bulkDeleteSuccess(deletedIds ?? []);
      notifyDataChanged('accounts');
    },
  });

  const changePage = (page: number) => (searchResult.active ? searchResult.setPage(page) : bulkActions.handlePageChange(page));

  const handleConfirmDelete = async () => {
    await store.confirmDelete();
    notifyDataChanged('accounts');
  };

  const handleCreateDefaults = async () => {
    setCreatingDefaults(true);
    try {
      const { count } = (await createDefaultAccounts()).data;
      toast.success(count > 0 ? t('accounts.list.initSuccess', { count }) : t('accounts.list.initExist'));
      setShowDefaultsConfirm(false);
      await useAccountStoreBase.getState().refreshData();
      notifyDataChanged('accounts');
    } catch (error) {
      toast.error(apiErrorMessage(error, 'accounts.list.initError'));
    } finally {
      setCreatingDefaults(false);
    }
  };

  const addButton = (
    <button type="button" onClick={() => setDialogAccount(null)} className={btnPrimary}>
      {t('accounts.list.addButton')}
    </button>
  );
  const defaultsButton = (
    <button type="button" onClick={() => setShowDefaultsConfirm(true)} className={btnPrimary}>
      {t('accounts.list.initializeButton')}
    </button>
  );

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <div className="mb-4">
        <div className="flex flex-wrap items-baseline justify-between gap-y-3">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('accounts.list.title')}</h1>
          <div className="flex items-center space-x-3 whitespace-nowrap">
            {admin && (
              <MultiBulkActionButton
                selectedCount={bulkActions.selectedItems.length}
                isDeleting={bulkActions.isBulkDeleting}
                onDelete={bulkActions.handleBulkDelete}
              />
            )}
            {addButton}
            {/* 不常用的管理功能收進「⋯」，放主要按鈕後面（R24、R31） */}
            {admin && (
              <MoreMenu
                variant="icon"
                label={t('accounts.list.moreActions')}
                items={[{ key: 'defaults', label: t('accounts.list.initializeButton'), onClick: () => setShowDefaultsConfirm(true) }]}
              />
            )}
          </div>
        </div>
      </div>

      <div className="mb-4 flex flex-col md:flex-row gap-3">
        <div className="md:w-80">
          <ListSearchInput value={searchTerm} onChange={setSearchTerm} placeholder={t('accounts.list.searchPlaceholder')} />
        </div>
        <div className="md:w-48">
          <CustomSelect
            value={typeFilter}
            onChange={value => setTypeFilter(value as TypeFilter)}
            options={TYPE_FILTERS.map(value => ({ value, label: value === 'ALL' ? t('accounts.list.allTypes') : t(`accounts.types.${value}`) }))}
          />
        </div>
        {hasFilters && (
          <button
            type="button"
            onClick={() => { setSearchTerm(''); setTypeFilter('ALL'); }}
            className="h-10 text-sm font-medium text-fg-secondary hover:text-fg self-start md:self-auto"
          >
            {t('common.filters.clear')}
          </button>
        )}
      </div>

      <ListPagination pagination={listPagination} onPageChange={changePage} isLoading={listLoading} className="mb-3" />

      {(isLoading && accounts.length === 0 && !searchResult.active) || listLoading ? (
        <TableSkeleton rows={10} columns={COLUMNS} showHeader={true} />
      ) : (
        <div className="bg-surface shadow dark:shadow-none dark:border dark:border-line overflow-hidden rounded-lg">
          <div className="overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <Table style={{ tableLayout: 'fixed', minWidth: '760px' }}>
              <TableHeader>
                <TableRow>
                  <BulkSelectHeader
                    userRole={userRole}
                    allSelected={bulkActions.allSelected}
                    onToggleAll={bulkActions.toggleSelectAll}
                    hasItems={listItems.length > 0}
                  />
                  <TableHead className="w-28">{t('accounts.table.code')}</TableHead>
                  <TableHead>{t('accounts.table.name')}</TableHead>
                  <TableHead className="w-24">{t('accounts.table.type')}</TableHead>
                  <TableHead numeric className="w-44">{t('accounts.table.balance')}</TableHead>
                  <TableHead className="w-32">{t('accounts.table.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {listItems.length === 0 ? (
                  <ListEmptyRow
                    colSpan={COLUMNS + 1}
                    filtered={hasFilters}
                    title={t('accounts.list.noAccounts')}
                    hint={admin ? t('accounts.list.emptyHint') : undefined}
                    action={admin ? defaultsButton : addButton}
                  />
                ) : (
                  listItems.map(account => {
                    const used = isAccountUsed(account);
                    return (
                      <TableRow key={account.id} className={pendingRowClass(account)}>
                        <BulkSelectCell
                          userRole={userRole}
                          isSelected={bulkActions.selectedItems.includes(account.id)}
                          onToggle={() => bulkActions.toggleItemSelection(account.id)}
                          canDelete={admin && !used}
                          disabledReason={used ? t('accounts.list.cannotDeleteUsed') : undefined}
                        />
                        <TableCell className="tabular-nums font-medium text-fg">{account.code}</TableCell>
                        <TableCell className="truncate" title={account.description || account.name}>
                          {account.name}
                          {account.description && <span className="block truncate text-[13px] text-fg-secondary">{account.description}</span>}
                        </TableCell>
                        <TableCell>{t(`accounts.types.${account.type}`)}</TableCell>
                        <TableCell numeric><Amount value={Number(account.balance ?? 0)} /></TableCell>
                        <TableCell>
                          <RowActions>
                            <RowAction onClick={() => setDialogAccount(account)}>{t('common.actions.edit')}</RowAction>
                            {admin && (
                              <RowAction
                                tone="danger"
                                disabled={used}
                                title={used ? t('accounts.list.cannotDeleteUsed') : undefined}
                                onClick={() => store.handleDelete(account.id)}
                              >
                                {t('common.actions.delete')}
                              </RowAction>
                            )}
                          </RowActions>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <ListPagination pagination={listPagination} onPageChange={changePage} isLoading={listLoading} className="mt-6" />

      <ConfirmModal
        isOpen={showDeleteConfirmModal}
        onClose={store.closeDeleteModal}
        onConfirm={handleConfirmDelete}
        title={t('accounts.list.confirmDeleteTitle')}
        message={t('accounts.list.confirmDeleteMessage')}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={isDeleting}
        loadingText={t('common.status.deleting')}
        t={t}
      />

      <ConfirmModal
        isOpen={bulkActions.showBulkDeleteModal}
        onClose={bulkActions.closeBulkDeleteModal}
        onConfirm={bulkActions.confirmBulkDelete}
        title={t('accounts.list.confirmBulkDeleteTitle', { count: bulkActions.selectedItems.length })}
        message={t('accounts.list.confirmBulkDeleteMessage', { count: bulkActions.selectedItems.length })}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={bulkActions.isBulkDeleting}
        loadingText={t('common.status.deleting')}
        t={t}
      />

      <ConfirmModal
        isOpen={showDefaultsConfirm}
        onClose={() => setShowDefaultsConfirm(false)}
        onConfirm={handleCreateDefaults}
        title={t('accounts.initDialog.title')}
        message={t('accounts.initDialog.description')}
        confirmText={t('accounts.initDialog.initialize')}
        cancelText={t('common.actions.cancel')}
        isLoading={creatingDefaults}
        loadingText={t('common.status.creating')}
        t={t}
      />

      {dialogAccount !== undefined && <AccountDialog account={dialogAccount ?? undefined} onClose={() => setDialogAccount(undefined)} />}
    </div>
  );
};

export default AccountListPage;
